import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { FaTimes, FaTimesCircle } from 'react-icons/fa';
import { getDevConfig } from '../../config/devConfig';
import emailjsConfig from '../../config/emailjsConfig';
import { FocusTrap } from '../../utils';
import useAutoHeightAdjustment from '../../hooks/useAutoHeightAdjustment';
import useScrollableDetection from '../../hooks/useScrollableDetection';
import ReCaptchaModal from './ReCaptchaModal';
import { 
  allServices, 
  validateForm, 
  focusFirstError, 
  getDefaultFormState, 
  getDefaultFormErrors 
} from '../../utils/formUtils';
import { sendEmail } from '../../utils/emailService';
import { useFormValidation } from '../../hooks/useFormValidation';
import '../forms/Contact.css';
import './CheckboxContainer.css';

const ContactModal = memo(({ isOpen, onClose, selectedService = null, formTitle = "Request a Free Quote", formSubtitle = "Fill out the form below and we'll get back to you within 24 hours." }) => {
  const SHOW_TEST_BUTTON = getDevConfig('SHOW_TEST_BUTTON');
  const modalRef = useRef(null);
  const focusTrapRef = useRef(null);
  const isInteractingWithFormRef = useRef(false);
  
  // Use the new auto height adjustment hook
  const { sameHeightRef, refresh: refreshHeights } = useAutoHeightAdjustment({
    containerSelector: '.contact-modal-content',
    elementSelector: '[data-same-height]',
    debounceDelay: 200,
    observeChildList: true,
    observeAttributes: true,
    observeCharacterData: true,
    subtree: true
  });
  
  // Hook to detect if modal content is scrollable
  const { ref: contentRef, isScrollable } = useScrollableDetection(isOpen);
  
  // Use custom form validation hook
  const {
    formData,
    formErrors,
    setFormData,
    setFormErrors,
    handleChange,
    handleKeyDown,
    clearFieldError
  } = useFormValidation(getDefaultFormState(), getDefaultFormErrors());
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [successMessageFading, setSuccessMessageFading] = useState(false);
  const [showCaptchaModal, setShowCaptchaModal] = useState(false);
  const [additionalFields, setAdditionalFields] = useState([]);
  const [serviceSearch, setServiceSearch] = useState('');
  const [showServiceResults, setShowServiceResults] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);
  const successTimeoutRef = useRef(null);
  const selectedServicesRef = useRef(null);
  const serviceSearchRef = useRef(null);
  const processedSelectedServiceRef = useRef(null);
  const effectRunCountRef = useRef(0);

  // Get available services (exclude already selected ones)
  const availableServices = useMemo(() => {
    return allServices.filter(service => 
      !formData.services.includes(service.value)
    );
  }, [formData.services]);

  // Get filtered services based on search input
  const filteredServices = useMemo(() => {
    if (!serviceSearch.trim()) return availableServices;
    return availableServices.filter(service =>
      service.label.toLowerCase().includes(serviceSearch.toLowerCase())
    );
  }, [availableServices, serviceSearch]);

  // Handle adding a service to the selected list
  const handleAddService = useCallback((serviceValue) => {
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, serviceValue]
    }));
    
    // Clear error when user selects a service
    if (formErrors.services) {
      setFormErrors(prev => ({
        ...prev,
        services: ''
      }));
    }
    
    // Trigger height refresh after service is added
    setTimeout(() => {
      if (refreshHeights) {
        refreshHeights();
      }
    }, 100);
  }, [formErrors.services, refreshHeights]);

  // Handle removing a service from the selected list
  const handleRemoveService = useCallback((serviceValue) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter(service => service !== serviceValue)
    }));
    
    // Trigger height refresh after service is removed
    setTimeout(() => {
      if (refreshHeights) {
        refreshHeights();
      }
    }, 100);
  }, [refreshHeights]);

  // Handle adding additional form fields
  const handleAddField = () => {
    const newField = {
      id: Date.now(),
      type: 'text',
      name: `additional_${Date.now()}`,
      placeholder: 'Additional Information',
      label: 'Additional Field'
    };
    setAdditionalFields(prev => [...prev, newField]);
  };

  // Handle removing additional form fields
  const handleRemoveField = (fieldId) => {
    setAdditionalFields(prev => prev.filter(field => field.id !== fieldId));
  };

  // Handle additional field changes
  const handleAdditionalFieldChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [`additional_${fieldId}`]: value
    }));
  };

  // Handle service search input change
  const handleServiceSearchChange = (e) => {
    const value = e.target.value;
    setServiceSearch(value);
    // Always show results when there are available services, regardless of search input
    setShowServiceResults(availableServices.length > 0);
    setSelectedOptionIndex(-1); // Reset selection when typing
    
    // Clear services error when user starts typing
    if (formErrors.services) {
      setFormErrors(prev => ({
        ...prev,
        services: ''
      }));
    }
  };

  // Handle clearing service search
  const handleClearServiceSearch = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setServiceSearch('');
    setShowServiceResults(false);
    setSelectedOptionIndex(-1);
    // Refocus the input after clearing
    if (serviceSearchRef.current) {
      serviceSearchRef.current.focus();
    }
  };

  // Handle service search input focus
  const handleServiceSearchFocus = () => {
    // Always show results when focused if there are available services
    setShowServiceResults(availableServices.length > 0);
    setSelectedOptionIndex(-1); // Reset selection on focus
  };

  // Handle service search input blur
  const handleServiceSearchBlur = (e) => {
    // Check if the blur is caused by clicking on a service result
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && relatedTarget.classList.contains('service-search-result')) {
      return; // Don't hide results if clicking on a service option
    }
    
    // Delay hiding results to allow for click events
    setTimeout(() => {
      setShowServiceResults(false);
      setSelectedOptionIndex(-1); // Reset selection on blur
    }, 200);
  };

  // Handle service selection from search results
  const handleServiceSelect = (serviceValue) => {
    handleAddService(serviceValue);
    setServiceSearch('');
    setSelectedOptionIndex(-1); // Reset selection
    
    // Keep dropdown open and maintain focus for continued selection
    setTimeout(() => {
      if (serviceSearchRef.current) {
        serviceSearchRef.current.focus();
        // Always show results - the availableServices will update automatically
        // due to the useMemo dependency on formData.services
        setShowServiceResults(true);
      }
    }, 100);
  };

  // Handle keyboard navigation for search results
  const handleServiceSearchKeyDown = (e) => {
    const currentResults = filteredServices.length > 0 ? filteredServices : availableServices;
    
    if (e.key === 'Escape') {
      setShowServiceResults(false);
      setServiceSearch('');
      setSelectedOptionIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (currentResults.length > 0) {
        const nextIndex = selectedOptionIndex < currentResults.length - 1 ? selectedOptionIndex + 1 : 0;
        setSelectedOptionIndex(nextIndex);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (currentResults.length > 0) {
        const prevIndex = selectedOptionIndex > 0 ? selectedOptionIndex - 1 : currentResults.length - 1;
        setSelectedOptionIndex(prevIndex);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedOptionIndex >= 0 && selectedOptionIndex < currentResults.length) {
        const selectedService = currentResults[selectedOptionIndex];
        handleServiceSelect(selectedService.value);
      }
    }
    // Tab key is not handled here - it will naturally move to the next focusable element
  };

  // Track form interactions to prevent modal close
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (e) => {
      // Check if mousedown is on any form element
      const target = e.target;
      if (target.closest('input') || 
          target.closest('textarea') || 
          target.closest('select') || 
          target.closest('button') ||
          target.closest('.contact-modal-content')) {
        isInteractingWithFormRef.current = true;
      }
    };

    const handleMouseUp = () => {
      // Reset interaction flag after a short delay
      setTimeout(() => {
        isInteractingWithFormRef.current = false;
      }, 100);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchstart', handleMouseDown);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchstart', handleMouseDown);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isOpen]);

  // Handle modal state changes (scroll prevention and focus trap)
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Store original overflow values
      const originalBodyOverflow = document.body.style.overflow;
      const originalHtmlOverflow = document.documentElement.style.overflow;
      
      // Prevent body and html scroll
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      // Initialize and activate focus trap
      focusTrapRef.current = new FocusTrap(modalRef.current);
      focusTrapRef.current.activate();
      
      // Handle ESC key to close modal
      const handleEscapeKey = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      
      // Add ESC key listener
      document.addEventListener('keydown', handleEscapeKey);
      
      return () => {
        // Remove ESC key listener
        document.removeEventListener('keydown', handleEscapeKey);
        
        // Restore original overflow when modal closes
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalHtmlOverflow;
        
        // Deactivate and cleanup focus trap
        if (focusTrapRef.current) {
          focusTrapRef.current.deactivate();
          focusTrapRef.current = null;
        }
      };
    }
  }, [isOpen, onClose]);

  // Refresh same-height when status messages change
  useEffect(() => {
    if (refreshHeights) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        refreshHeights();
      }, 50);
    }
  }, [submitStatus, refreshHeights]);

  // Refresh same-height when services change
  useEffect(() => {
    if (refreshHeights) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        refreshHeights();
      }, 100);
    }
  }, [formData.services, refreshHeights]);

  // Additional effect to watch selectedServicesRef changes
  useEffect(() => {
    if (selectedServicesRef.current && refreshHeights) {
      const observer = new MutationObserver(() => {
        // Debounced refresh for service tag changes
        setTimeout(() => {
          refreshHeights();
        }, 50);
      });

      observer.observe(selectedServicesRef.current, {
        childList: true,
        subtree: true,
        characterData: true
      });

      return () => {
        observer.disconnect();
      };
    }
  }, [formData.services.length, refreshHeights]);

  // Auto-populate services field when selectedService is provided
  useEffect(() => {
    // Only run this effect when the modal is open and we have a selected service
    if (!isOpen || !selectedService) {
      // Reset the processed ref when modal closes
      if (!isOpen) {
        processedSelectedServiceRef.current = null;
      }
      return;
    }
    
    // Check if we've already processed this selectedService
    if (processedSelectedServiceRef.current === selectedService) {
      return;
    }
    
    // Mark this selectedService as processed
    processedSelectedServiceRef.current = selectedService;
    
    // Find the service in allServices that matches the selected service title
    const selectedTitle = selectedService.title.toLowerCase();
    
    // Create a mapping for service name variations
    const serviceMapping = {
        'full service renovations': 'full-service',
        'foundations': 'foundations',
        'roofing': 'roofing',
        'kitchens': 'kitchens',
        'bathrooms': 'bathrooms',
        'basements': 'basements',
        'siding': 'siding',
        'additions': 'additions',
        'full gut rehabs': 'gut-rehabs',
        'plumbing': 'plumbing',
        'framing': 'framing',
        'cement': 'cement',
        'hvac': 'hvac',
        'electrical': 'electrical',
        'painting': 'painting',
        'drywall': 'drywall',
        'windows & doors': 'windows', // Map to windows since they're combined
        'fences and decks': 'decking', // Map to decking
        'tile work': 'tiles'
    };
    
    // Try exact match first, then try the mapping
    let matchingService = allServices.find(service => 
      service.label.toLowerCase() === selectedTitle
    );
    
    // If no exact match, try the mapping
    if (!matchingService && serviceMapping[selectedTitle]) {
      const mappedValue = serviceMapping[selectedTitle];
      matchingService = allServices.find(service => service.value === mappedValue);
    }
    
    // Get current form data to check if service is already added
    const currentServices = formData.services;
    if (matchingService && !currentServices.includes(matchingService.value)) {
      // Add the service to the form data
      setFormData(prev => ({
        ...prev,
        services: [...prev.services, matchingService.value]
      }));
      
      // Clear any services error
      if (formErrors.services) {
        setFormErrors(prev => ({
          ...prev,
          services: ''
        }));
      }
      
      // Trigger height refresh after service is added
      setTimeout(() => {
        if (refreshHeights) {
          refreshHeights();
        }
      }, 100);
    }
  }, [selectedService, isOpen, formData.services]); // Include formData.services since we use it

  // Cleanup effect - reset form and refs when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset the processed selected service ref
      processedSelectedServiceRef.current = null;
      // Reset the effect run counter
      effectRunCountRef.current = 0;
      // Clear any success/error messages
      setSubmitStatus(null);
      setSuccessMessageFading(false);
      // Clear service search
      setServiceSearch('');
      setShowServiceResults(false);
      setSelectedOptionIndex(-1);
      // Clear additional fields
      setAdditionalFields([]);
      // Clear reCAPTCHA modal
      setShowCaptchaModal(false);
      // Reset form data to default state
      setFormData({
        name: '',
        email: '',
        phone: '',
        services: [],
        message: '',
        smsConsent: false
      });
      // Clear form errors
      setFormErrors({
        name: '',
        email: '',
        phone: '',
        services: '',
        message: '',
        smsConsent: ''
      });
    }
  }, [isOpen]);

  // Auto-remove success message after 5 seconds
  useEffect(() => {
    if (submitStatus === 'success') {
      // Clear any existing timeout
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      
      // Start fade-out after 4.5 seconds
      successTimeoutRef.current = setTimeout(() => {
        setSuccessMessageFading(true);
        
        // Remove message completely after fade animation
        setTimeout(() => {
          setSubmitStatus(null);
          setSuccessMessageFading(false);
        }, 500); // Match CSS animation duration
      }, 4500);
    }
    
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, [submitStatus]);



  const sendEmailData = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Convert services array to comma-separated string
      const servicesText = formData.services
        .map(serviceValue => {
          const service = allServices.find(s => s.value === serviceValue);
          return service ? service.label : serviceValue;
        })
        .join(', ');

      // Build additional fields text
      const additionalFieldsText = additionalFields
        .map(field => {
          const value = formData[`additional_${field.id}`] || '';
          return value ? `${field.label}: ${value}` : '';
        })
        .filter(text => text)
        .join('\n');

      // Create form data with additional fields
      const formDataWithMessage = {
        ...formData,
        message: formData.message + (additionalFieldsText ? `\n\nAdditional Information:\n${additionalFieldsText}` : '')
      };
      
      // Prepare email data for EmailJS
      const emailData = {
        name: formDataWithMessage.name,
        email: formDataWithMessage.email,
        phone: formDataWithMessage.phone,
        service: servicesText,
        message: formDataWithMessage.message,
        smsConsent: formDataWithMessage.smsConsent ? 'Yes' : 'No',
        type: 'contact'
      };
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Email sending timeout')), 30000); // 30 second timeout
      });
      
      const result = await Promise.race([
        sendEmail(emailData, 'contact'),
        timeoutPromise
      ]);
      
      if (result.success) {
        setIsSubmitting(false);
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          services: [],
          message: '',
          smsConsent: false
        });
        setAdditionalFields([]);
        
        // Focus on success message after a short delay to ensure it's rendered
        setTimeout(() => {
          const successMessage = document.querySelector('.contact-modal .status-message.success');
          if (successMessage) {
            successMessage.focus();
            successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      setIsSubmitting(false);
      setSubmitStatus('error');
    }
  }, [formData, additionalFields]);

  const handleCaptchaVerify = useCallback(async (value) => {
    try {
      // CAPTCHA completed, now send the email
      await sendEmailData();
    } catch (error) {
      console.error('Error in CAPTCHA verification:', error);
      setSubmitStatus('error');
    } finally {
      setShowCaptchaModal(false);
    }
  }, [sendEmailData]);

  const validateFormData = () => {
    const validation = validateForm(formData);
    setFormErrors(validation.errors);
    return { isValid: validation.isValid, errors: validation.errors };
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Validate form using shared utility
    const validation = validateFormData();
    
    if (!validation.isValid) {
      setSubmitStatus('validation-error');
      focusFirstError(validation.errors, { serviceSearch: serviceSearchRef.current }, 'modal');
      return;
    }
    
    // Show reCAPTCHA modal for verification
    setShowCaptchaModal(true);
  }, [validateFormData, formData]);

  const handleBackdropClick = useCallback((e) => {
    // Don't close if user was interacting with form elements
    if (isInteractingWithFormRef.current) {
      return;
    }
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div 
      className="contact-modal-overlay" 
      onClick={handleBackdropClick}
    >
      <div 
        className={`contact-modal-content pad-h-xs-3 ${isScrollable ? 'scrollable' : ''}`}
        ref={(el) => {
          modalRef.current = el;
          contentRef.current = el;
        }}
      >
        <button className="contact-modal-close" onClick={onClose} aria-label="Close contact modal">
          <FaTimes aria-hidden="true" />
        </button>
        
        <div className="contact-form-card modal-form-card pad-t-12">
          <div className="form-header">
            <h3>{formTitle}</h3>
            <p className="pad-h-xs-4 margin-b-1">{formSubtitle}</p>
          </div>
          
          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div>
              <div className="form-group">
                <label htmlFor="modal-name" className="form-label">Full Name</label>
                <input
                  type="text"
                  id="modal-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Full Name"
                  className={`form-input ${formErrors.name ? 'error' : ''}`}
                  aria-describedby={formErrors.name ? 'name-error' : undefined}
                  aria-invalid={!!formErrors.name}
                  title="Please enter only letters"
                  required
                />
                {formErrors.name && (
                  <span id="name-error" className="form-error" role="alert">{formErrors.name}</span>
                )}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="modal-email" className="form-label">Email Address</label>
                <input
                  type="email"
                  id="modal-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address"
                  className={`form-input ${formErrors.email ? 'error' : ''}`}
                  aria-describedby={formErrors.email ? 'email-error' : undefined}
                  aria-invalid={!!formErrors.email}
                  required
                />
                {formErrors.email && (
                  <span id="email-error" className="form-error" role="alert">{formErrors.email}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="modal-phone" className="form-label">Phone Number</label>
                <input
                  type="tel"
                  id="modal-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Phone Number"
                  className={`form-input ${formErrors.phone ? 'error' : ''}`}
                  aria-describedby={formErrors.phone ? 'phone-error' : undefined}
                  aria-invalid={!!formErrors.phone}
                  title="Please enter only numbers"
                  required
                />
                {formErrors.phone && (
                  <span id="phone-error" className="form-error" role="alert">{formErrors.phone}</span>
                )}
              </div>
            </div>

            <div>
            <div className="form-group">
                {/* Selected Services Tags */}
                {formData.services.length > 0 && (
                  <div className="selected-services-container">
                    <label className="selected-services-label">Selected Services:</label>
                    <div className="selected-services" ref={selectedServicesRef}>
                      {formData.services.map(serviceValue => {
                        const service = allServices.find(s => s.value === serviceValue);
                        return (
                          <div key={serviceValue} className="service-tag">
                            <span className="service-tag-text">{service?.label || serviceValue}</span>
                            <button
                              type="button"
                              className="service-tag-remove"
                              onClick={() => handleRemoveService(serviceValue)}
                              aria-label={`Remove ${service?.label || serviceValue}`}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Service Search Input */}
                {availableServices.length > 0 && (
                  <div 
                    className="service-search-container"
                    onClick={(e) => {
                      // Prevent container clicks from triggering focus
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <label htmlFor="service-search-modal" className="form-label">
                      Services Needed
                    </label>
                    <div className="service-search-input-wrapper">
                      <input
                        ref={serviceSearchRef}
                        type="text"
                        id="service-search-modal"
                        name="service-search"
                        value={serviceSearch}
                        onChange={handleServiceSearchChange}
                        onFocus={handleServiceSearchFocus}
                        onBlur={handleServiceSearchBlur}
                        onKeyDown={handleServiceSearchKeyDown}
                        placeholder="Search and add services..."
                        className={`service-search-input ${formErrors.services ? 'error' : ''}`}
                        autoComplete="off"
                        aria-expanded={showServiceResults}
                        aria-haspopup="listbox"
                        aria-autocomplete="list"
                        aria-describedby={formErrors.services ? 'services-error' : 'services-help'}
                        aria-invalid={!!formErrors.services}
                        role="combobox"
                      />
                      {showServiceResults && (
                        <button
                          type="button"
                          className="service-search-clear-btn"
                          onClick={handleClearServiceSearch}
                          aria-label="Clear search"
                          title="Clear search"
                        >
                          <FaTimesCircle />
                        </button>
                      )}
                    </div>
                    
                    <div id="services-help" className="form-help-text sr-only">
                      Type to search and select services. You can add multiple services.
                    </div>
                    
                    {formErrors.services && (
                      <span id="services-error" className="form-error" role="alert">{formErrors.services}</span>
                    )}
                    
                    {/* Search Results Dropdown */}
                    {(showServiceResults || (serviceSearch.trim() && filteredServices.length === 0)) && (
                      <div 
                        className="service-search-results"
                        role="listbox"
                        aria-label="Available services"
                        id="service-search-listbox-contact"
                      >
                        {serviceSearch.trim() && filteredServices.length === 0 ? (
                          <div 
                            className="service-search-no-results"
                            role="status"
                            aria-live="polite"
                          >
                            No services found for "{serviceSearch}"
                          </div>
                        ) : (
                          (serviceSearch.trim() ? filteredServices : availableServices).map((service, index) => (
                            <div
                              key={service.value}
                              className={`service-search-result ${index === selectedOptionIndex ? 'selected' : ''}`}
                              onClick={() => handleServiceSelect(service.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleServiceSelect(service.value);
                                }
                              }}
                              role="option"
                              tabIndex={-1}
                              aria-selected={index === selectedOptionIndex}
                              ref={(el) => {
                                if (index === selectedOptionIndex && el) {
                                  el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                                }
                              }}
                            >
                              {service.label}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {availableServices.length === 0 && formData.services.length > 0 && (
                  <div className="no-services-message">
                    All services have been selected
                  </div>
                )}
                
              </div>
            </div>
            
            {/* Additional Dynamic Fields */}
            {additionalFields.map(field => (
              <div key={field.id} className="form-group">
                <label htmlFor={field.name}>{field.label}</label>
                <div className="additional-field-container">
                  <input
                    type={field.type}
                    id={field.name}
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={(e) => handleAdditionalFieldChange(field.id, e.target.value)}
                    placeholder={field.placeholder}
                    className="additional-field-input"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveField(field.id)}
                    className="additional-field-remove"
                    title="Remove field"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
            
            <div className="form-group">
              <label htmlFor="modal-message" className="form-label">Message</label>
              <textarea
                id="modal-message"
                name="message"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us the address you need service at and provide any additional details here..."
                className={`form-textarea ${formErrors.message ? 'error' : ''}`}
                aria-describedby={formErrors.message ? 'message-error' : undefined}
                aria-invalid={!!formErrors.message}
                required
              ></textarea>
              {formErrors.message && (
                <span id="message-error" className="form-error" role="alert">{formErrors.message}</span>
              )}
            </div>

            {/* SMS Consent Checkbox */}
            <div className="form-group">
              <div className="checkbox-container pad-h-3">
                <input
                  type="checkbox"
                  id="modal-sms-consent"
                  name="smsConsent"
                  checked={formData.smsConsent || false}
                  onChange={handleChange}
                  className={`sms-consent-checkbox ${formErrors.smsConsent ? 'error' : ''}`}
                  aria-describedby="sms-consent-disclosure"
                  aria-invalid={!!formErrors.smsConsent}
                />
                <label htmlFor="modal-sms-consent" className="sms-consent-label">
                  <span className="checkbox-text">
                    <strong>Receive SMS Updates</strong>
                    <span className="disclosure-text">
                    By checking this box, you agree to receive SMS messages from RC Roofing. Message and data rates may apply. Frequency may vary. Reply STOP to unsubscribe or HELP for info. Messages may include updates, reminders, notifications, and promotions. View our Terms and Privacy Policy.<br />View our{' '}
                      <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">terms and privacy policy</a>.
                    </span>
                  </span>
                </label>
              </div>
              {formErrors.smsConsent && (
                <span id="sms-consent-error" className="form-error" role="alert">{formErrors.smsConsent}</span>
              )}
            </div>
            
            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
              >
                <span className="button-text">
                  {isSubmitting ? 'Sending...' : 'SEND MESSAGE'}
                </span>
              </button>
              
              {/* TESTING: Button to add additional fields */}
              {SHOW_TEST_BUTTON && (
                <button 
                  type="button" 
                  className="test-add-field-button"
                  onClick={handleAddField}
                >
                  Add Field
                </button>
              )}
              
              {/* TESTING: Button to add a service */}
              {SHOW_TEST_BUTTON && (
                <button 
                  type="button" 
                  className="test-add-service-button"
                  onClick={() => {
                    if (availableServices.length > 0) {
                      handleAddService(availableServices[0].value);
                    }
                  }}
                  disabled={availableServices.length === 0}
                  style={{
                    background: '#17a2b8',
                    color: 'white',
                    border: 'none',
                    padding: '18px 25px',
                    borderRadius: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    marginLeft: '15px',
                    opacity: availableServices.length === 0 ? 0.5 : 1
                  }}
                  onMouseOver={(e) => {
                    if (availableServices.length > 0) {
                      e.target.style.background = '#138496';
                      e.target.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (availableServices.length > 0) {
                      e.target.style.background = '#17a2b8';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  Add Service
                </button>
              )}
              
              {/* TESTING: Button to simulate API error */}
              {SHOW_TEST_BUTTON && (
                <button 
                  type="button" 
                  className="test-error-button"
                  onClick={() => {
                    setIsSubmitting(true);
                    setTimeout(() => {
                      setIsSubmitting(false);
                      setSubmitStatus('error');
                    }, 1000);
                  }}
                  disabled={isSubmitting}
                >
                  Test Error
                </button>
              )}
              
              {/* TESTING: Button to test email with current form data */}
              {SHOW_TEST_BUTTON && (
                <button 
                  type="button" 
                  className="test-email-button"
                  onClick={async () => {
                    try {
                      const servicesText = formData.services
                        .map(serviceValue => {
                          const service = allServices.find(s => s.value === serviceValue);
                          return service ? service.label : serviceValue;
                        })
                        .join(', ');

                      const emailData = {
                        name: formData.name,
                        email: formData.email,
                        phone: formData.phone,
                        service: servicesText,
                        message: formData.message,
                        type: 'contact'
                      };
                      
                      const result = await sendEmail(emailData, 'contact');
                    } catch (error) {
                      console.error('🧪 Test email error:', error);
                    }
                  }}
                  disabled={isSubmitting}
                  style={{
                    background: '#ff6b35',
                    color: 'white',
                    border: 'none',
                    padding: '18px 25px',
                    borderRadius: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    marginLeft: '15px'
                  }}
                >
                  Test Email
                </button>
              )}
            </div>
            
            <div className="form-disclaimer">
              <p>By submitting this form, you agree to receive communications from RC Roofing. We respect your privacy and will never share your information.</p>
            </div>
            
            {/* Developer indicator */}
            {SHOW_TEST_BUTTON && (
              <div className="dev-indicator">
                <small>🔧 Developer Mode: Test buttons enabled - Try adding/removing fields and services to see automatic height adjustment</small>
              </div>
            )}
            
            {/* Success Message */}
            {submitStatus === 'success' && (
              <div 
                className={`status-message success ${successMessageFading ? 'fade-out' : ''}`}
                tabIndex="-1"
                role="alert"
                aria-live="polite"
                style={{ 
                  background: 'rgba(40, 167, 69, 0.1)', 
                  border: '1px solid rgba(40, 167, 69, 0.3)', 
                  color: '#28a745',
                  padding: '15px',
                  borderRadius: '8px',
                  margin: '15px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <span className="status-icon">✓</span>
                <span className="status-text">Your message has been sent successfully!</span>
              </div>
            )}
            
            {/* Error Message */}
            {submitStatus === 'error' && (
              <div 
                className="status-message error"
                tabIndex="-1"
                role="alert"
                aria-live="polite"
                style={{ 
                  background: 'rgba(220, 38, 38, 0.1)', 
                  border: '1px solid rgba(220, 38, 38, 0.3)', 
                  color: '#dc2626',
                  padding: '15px',
                  borderRadius: '8px',
                  margin: '15px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <span className="status-icon">✗</span>
                <span className="status-text">Sorry, there was an error sending your message. Please try again.</span>
              </div>
            )}
            
            
          </form>
        </div>
      </div>
      
      <ReCaptchaModal 
        isOpen={showCaptchaModal}
        onVerify={handleCaptchaVerify}
        isLoading={isSubmitting}
      />
    </div>
  );
});

ContactModal.displayName = 'ContactModal';

export default ContactModal;
